/**
 * Meridian Gazebo Bundle product template — isolated JS.
 * Only runs when [data-product-container].template-meridian-gazebo-bundle exists.
 */
const MERIDIAN_BUNDLE_MAIN_IMAGE_WIDTH = 1200;
const MERIDIAN_BUNDLE_THUMB_IMAGE_WIDTH = 128;
const MERIDIAN_LIGHTBOX_IMAGE_WIDTH = 2048;

const MERIDIAN_URL_ACCESSORY_HANDLES = (() => {
  if (Array.isArray(window.__meridianUrlAccessoryHandles) && window.__meridianUrlAccessoryHandles.length) {
    return [...window.__meridianUrlAccessoryHandles];
  }

  try {
    const accessoriesParam = new URLSearchParams(window.location.search).get('accessories');
    if (!accessoriesParam) return [];

    return accessoriesParam
      .split(',')
      .map((handle) => handle.trim().toLowerCase())
      .filter(Boolean);
  } catch {
    return [];
  }
})();

class MeridianGazeboBundleJS {
  constructor() {
    this.container = document.querySelector('[data-product-container].template-meridian-gazebo-bundle');
    if (!this.container) return;

    this._parseAccessoriesUrlParam();

    this.initMeridianAccordionToggles();
    this.initGallery();
    this.initVariantRatingSummary();
    this.initVariantProductTitle();
    this.initVariantBazaarvoiceReviews();
    this.initVariantRelatedProducts();
    this.initVariantQrImage();
    this.initVariantFeatures();
    this.initVariantStains();
    this.initVariantInstallation();
    this.initVariantAccessories();
    this.initVariantProductTabs();
    this.initMeridianBuyTogether();
    this.initQuickViewFeaturesReadMore();
    this.initVariantMainProductCommerce();
    this.initCustomBundleSwitch();
    this.initAccessoriesFromUrlParams();
  }

  _parseAccessoriesUrlParam() {
    this._urlAccessoryHandles = MERIDIAN_URL_ACCESSORY_HANDLES.length
      ? [...MERIDIAN_URL_ACCESSORY_HANDLES]
      : [];

    if (!this._urlAccessoryHandles.length) {
      const accessoriesParam = new URLSearchParams(window.location.search).get('accessories');
      if (accessoriesParam) {
        this._urlAccessoryHandles = accessoriesParam
          .split(',')
          .map((handle) => handle.trim().toLowerCase())
          .filter(Boolean);
      }
    }

    if (this._urlAccessoryHandles.length) {
      window.__meridianUrlAccessoryHandles = [...this._urlAccessoryHandles];
    }

    this._urlAccessoryHandlesDismissed = this._urlAccessoryHandlesDismissed || new Set();
  }

  initMeridianAccordionToggles() {
    if (this._meridianAccordionDelegated) return;
    this._meridianAccordionDelegated = true;

    const handleAccordionInteraction = (event) => {
      if (!(event.target instanceof Element)) return;

      const panel = event.target.closest('[data-meridian-collapsible]');
      if (!panel || !this.container.contains(panel)) return;

      const toggle = panel.querySelector('.panel_toggle');
      if (!toggle || !toggle.contains(event.target)) return;

      if (event.type === 'keydown') {
        if (event.key !== 'Enter' && event.key !== ' ') return;
      }

      if (event.target.closest('[data-modal-opener]')) return;
      if (event.target.closest('#installCheck')) return;

      const innerLink = event.target.closest('a[href]');
      if (innerLink && innerLink !== toggle && toggle.contains(innerLink)) return;

      event.preventDefault();
      event.stopPropagation();

      this.toggleMeridianCollapsiblePanel(panel);
    };

    this.container.addEventListener('click', handleAccordionInteraction);
    this.container.addEventListener('keydown', handleAccordionInteraction);
  }

  toggleMeridianCollapsiblePanel(panel) {
    if (!panel) return;

    const toggle = panel.querySelector('.panel_toggle');
    const isOpen = panel.classList.contains('open');

    if (isOpen) {
      toggle?.setAttribute('aria-expanded', 'false');
      panel.classList.remove('open');
      panel.querySelector('.toggle-content')?.classList.remove('open');
      return;
    }

    this.openCollapsiblePanel(panel);
  }

  initQuickViewFeaturesReadMore(root) {
    const scope = root instanceof Element ? root : this.container;
    if (!scope) return;

    if (window.__templateProductJS?._initProductFeaturesReadMore) {
      window.__templateProductJS._initProductFeaturesReadMore(scope, true);
      return;
    }

    scope.querySelectorAll('[data-product-features-read-more]').forEach((btn) => {
      const container =
        (btn.getAttribute('data-features-target') &&
          document.getElementById(btn.getAttribute('data-features-target'))) ||
        btn.previousElementSibling;
      const list = container?.querySelector('ul');
      if (!list) {
        btn.hidden = true;
        return;
      }

      const items = list.querySelectorAll('li');
      if (items.length <= 4) {
        btn.hidden = true;
        return;
      }

      items.forEach((item, index) => {
        item.style.display = index >= 4 ? 'none' : 'list-item';
      });
      btn.hidden = false;
      btn.onclick = () => {
        items.forEach((item) => {
          item.style.display = 'list-item';
        });
        btn.hidden = true;
      };
    });
  }

  escapeHtmlAttr(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  normalizeBundleImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  withBundleImageWidth(url, width) {
    const normalized = this.normalizeBundleImageUrl(url);
    if (!normalized) return '';

    if (/width=\d+/.test(normalized)) {
      return normalized.replace(/width=\d+/, `width=${width}`);
    }

    const separator = normalized.includes('?') ? '&' : '?';
    return `${normalized}${separator}width=${width}`;
  }

  getBundleMainImageUrl(url) {
    return this.withBundleImageWidth(url, MERIDIAN_BUNDLE_MAIN_IMAGE_WIDTH);
  }

  getBundleThumbImageUrl(url, width = MERIDIAN_BUNDLE_THUMB_IMAGE_WIDTH) {
    return this.withBundleImageWidth(url, width);
  }

  getBundleImageKey(url) {
    return this.normalizeBundleImageUrl(url)
      .replace(/([?&])width=\d+(&?)/g, (_, prefix, suffix) => (suffix ? prefix : ''))
      .replace(/[?&]$/, '');
  }

  // Mirrors the Liquid gallery markup so JS-built slides open in the lightbox (lightbox-zoom.js).
  getGalleryImageClass(zoomType) {
    const classes = [];
    if (zoomType === 'in_image_block') classes.push('image-magnify-hover');
    if (zoomType !== 'no_zoom') classes.push('lightbox-trigger');
    return classes.join(' ');
  }

  getGalleryLightboxAttrs(zoomType, { url, width, alt, index }) {
    if (zoomType === 'no_zoom' || !url) return '';

    const fullSrc = width ? this.withBundleImageWidth(url, width) : this.normalizeBundleImageUrl(url);
    return [
      `data-lightbox-src="${this.withBundleImageWidth(url, MERIDIAN_LIGHTBOX_IMAGE_WIDTH)}"`,
      `data-lightbox-src-full="${fullSrc}"`,
      width ? `data-lightbox-width="${width}"` : '',
      `data-lightbox-index="${index}"`,
      `data-lightbox-alt="${this.escapeHtmlAttr(alt)}"`,
    ].filter(Boolean).join(' ');
  }

  // Gallery slides are swapped and moved with Swiper callbacks disabled, so resync the lightbox state.
  syncGalleryLightbox() {
    // Re-apply the selected accessory / stain image (template-product.js) after the gallery was rebuilt or moved.
    window.__templateProductJS?._syncAccessoryGallerySlides?.(null);

    const mobileZoom = window.mobileGalleryZoom;
    if (mobileZoom?.carousel) mobileZoom.reset();

    const lightbox = window.productLightbox;
    if (!lightbox?.modal) return;
    lightbox.collectImages();
    lightbox.updateFullscreenBtn();
  }

  removeBundleSlidesFromSwiper(swiper) {
    if (!swiper?.slides?.length || typeof swiper.removeSlide !== 'function') return;

    const indices = [];
    Array.from(swiper.slides).forEach((slide, index) => {
      if (slide.hasAttribute('data-custom-bundle-slide')) {
        indices.push(index);
      }
    });

    indices.sort((a, b) => b - a).forEach((index) => {
      swiper.removeSlide(index);
    });

    if (indices.length && typeof swiper.update === 'function') {
      swiper.update();
    }
  }

  prependBundleToSwiperGallery(bundleData) {
    const gallerySlider = document.querySelector('[data-variant-gallery-slider]');
    if (!gallerySlider || !bundleData?.image) return false;

    const bundleBase = this.normalizeBundleImageUrl(bundleData.image);
    const bundleSrc = this.getBundleMainImageUrl(bundleData.image);
    const alt = bundleData.image_alt || bundleData.subheading || '';
    const containerWidth = gallerySlider.dataset.containerWidth || '1200px';
    const zoomType = gallerySlider.dataset.zoomType || '';

    const withBundleWidth = (url, width) => this.withBundleImageWidth(url || bundleBase, width);

    const mainSlideHtml = `
      <div class="swiper-slide d-flex align-items-center product--media" data-mediaID="custom-bundle-image" data-variant-gallery-slide data-custom-bundle-slide role="treeitem">
        <img
          src="${bundleSrc}"
          srcset="${withBundleWidth(bundleBase, 550)} 550w,
                  ${withBundleWidth(bundleBase, 750)} 750w,
                  ${withBundleWidth(bundleBase, 991)} 991w,
                  ${withBundleWidth(bundleBase, 1200)} 1200w,
                  ${withBundleWidth(bundleBase, 1600)} 1600w"
          preload="true" fetchpriority="high"
          data-variant-title="${alt}"
          sizes="(min-width: ${containerWidth}) calc(${containerWidth} / 2), 100vw"
          alt="${alt}"
          class="${this.getGalleryImageClass(zoomType)}"
          ${this.getGalleryLightboxAttrs(zoomType, { url: bundleBase, alt, index: -1 })}
        >
      </div>
    `;

    const thumbSlideHtml = `
      <div class="swiper-slide product-thumb--image" data-index="0" data-mediaID="custom-bundle-thumb" data-variant-gallery-slide data-custom-bundle-slide role="treeitem">
        <div class="thumb-item overflow-hidden">
          <img class="img-fluid"
            src="${withBundleWidth(bundleBase, 250)}"
            srcset="${withBundleWidth(bundleBase, 250)}"
            sizes="250px"
            loading="lazy"
            alt="${alt}"
          />
        </div>
      </div>
    `;

    const mainSwiper = this.getMainGallerySwiper();
    const thumbSwiper = this.getThumbGallerySwiper();

    this.removeBundleSlidesFromSwiper(mainSwiper);
    this.removeBundleSlidesFromSwiper(thumbSwiper);

    if (mainSwiper && typeof mainSwiper.prependSlide === 'function') {
      mainSwiper.prependSlide(mainSlideHtml);
      mainSwiper.update();
      mainSwiper.slideTo(0, 0, false);
    } else {
      const mainWrapper = gallerySlider.querySelector('[data-variant-gallery-main]');
      mainWrapper?.querySelector('[data-custom-bundle-slide]')?.remove();
      mainWrapper?.insertAdjacentHTML('afterbegin', mainSlideHtml);
    }

    if (thumbSwiper && typeof thumbSwiper.prependSlide === 'function') {
      thumbSwiper.prependSlide(thumbSlideHtml);
      thumbSwiper.update();
      thumbSwiper.slideTo(0, 0, false);
    } else {
      const thumbWrapper = document.querySelector('[data-variant-gallery-thumbs]');
      thumbWrapper?.querySelector('[data-custom-bundle-slide]')?.remove();
      thumbWrapper?.insertAdjacentHTML('afterbegin', thumbSlideHtml);
    }

    this.refreshSlider();
    return true;
  }

  removeBundleFromSwiperGallery() {
    // variant:changed fires on every product-form change; only reset the gallery when a bundle slide was removed.
    const hadBundleSlide = !!document.querySelector('[data-custom-bundle-slide]');
    this.removeBundleSlidesFromSwiper(this.getMainGallerySwiper());
    this.removeBundleSlidesFromSwiper(this.getThumbGallerySwiper());
    document.querySelectorAll('[data-custom-bundle-slide]').forEach((slide) => slide.remove());
    if (hadBundleSlide) this.refreshSlider();
    return true;
  }

  applyBundleToGallery(bundleData = null) {
    const gallerySlider = document.querySelector('[data-variant-gallery-slider]');

    if (!gallerySlider) return false;

    if (bundleData?.image) {
      const rendered = this.prependBundleToSwiperGallery(bundleData);
      if (rendered) {
        this._activeBundleImageKey = this.getBundleImageKey(bundleData.image);
      }
      return rendered;
    }

    this._activeBundleImageKey = null;
    return this.removeBundleFromSwiperGallery();
  }

  getPickerVariantId() {
    const variantJsonEl = document.querySelector('[data-variantJSON]');
    const picker = this.container?.querySelector('variant-radios, variant-selects');
    if (!variantJsonEl || !picker) return null;

    const variants = JSON.parse(variantJsonEl.textContent);
    let options = [];

    if (picker.tagName === 'VARIANT-SELECTS') {
      options = [...picker.querySelectorAll('.variant_selector')].map((select) => select.value);
    } else {
      options = [...picker.querySelectorAll('fieldset:not(.addon-fieldset)')].map((fieldset) => {
        const input = fieldset.querySelector('input:not(.addon-swatch):checked');
        return input ? input.value : null;
      });
    }

    if (options.some((option) => option == null)) return null;

    const match = variants.find((entry) =>
      entry.options.every((option, index) => option === options[index])
    );

    return match ? String(match.id) : null;
  }

  getSelectedVariantId() {
    const pickerVariantId = this.getPickerVariantId();
    if (pickerVariantId) return pickerVariantId;

    const currentVariantId = window.globalVariables?.product?.currentVariant?.id;
    return currentVariantId ? String(currentVariantId) : null;
  }

  bindVariantChange(handler) {
    const run = () => {
      const variantId = this.getSelectedVariantId();
      if (variantId) handler(variantId);
    };

    document.addEventListener('variant:changed', run);

    this.container.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;

      const isVariantInput =
        event.target.matches('input[type="radio"]') && event.target.closest('variant-radios');
      const isVariantSelect =
        event.target.matches('select, .variant_selector') && event.target.closest('variant-selects');

      if (isVariantInput || isVariantSelect) run();
    });

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  initGallery() {
    const galleryDataEl = document.querySelector('[data-variant-gallery-images]');
    const productMediaEl = document.querySelector('[data-productMedia]');
    if (!galleryDataEl) {
      console.warn('[BundleGallery] initGallery skipped: data-variant-gallery-images not found');
      return;
    }

    const variantGalleryMap = JSON.parse(galleryDataEl.textContent);

    const normalizeImageUrl = (url) => {
      if (!url) return '';
      if (url.startsWith('//')) return `https:${url}`;
      return url;
    };

    const withWidth = (url, width) => {
      if (!url) return '';
      const normalized = normalizeImageUrl(url);
      return normalized.includes('?') ? `${normalized}&width=${width}` : `${normalized}?width=${width}`;
    };

    const withBundleWidth = (url, width) => {
      if (!url) return '';
      const normalized = normalizeImageUrl(url);
      if (/width=\d+/.test(normalized)) {
        return normalized.replace(/width=\d+/, `width=${width}`);
      }
      return withWidth(normalized, width);
    };

    const fallbackImages = productMediaEl
      ? JSON.parse(productMediaEl.textContent)
          .filter((media) => media.media_type === 'image')
          .map((media) => {
            const src = media.preview_image || media.src || {};
            const width = src.width || 1500;
            const height = src.height || Math.round(width / (src.aspect_ratio || 1));
            const baseUrl = src.src || '';
            return {
              id: media.id,
              alt: media.alt || '',
              width,
              height,
              src: withWidth(baseUrl, 50),
              thumb: withWidth(baseUrl, 250),
              srcset350: withWidth(baseUrl, 350),
              srcset550: withWidth(baseUrl, 550),
              srcset750: withWidth(baseUrl, 750),
              srcset991: withWidth(baseUrl, 991),
              srcset1200: withWidth(baseUrl, 1200),
              srcset1500: withWidth(baseUrl, 1500),
              srcsetFull: baseUrl
            };
          })
      : [];

    const buildSrcset = (image) => {
      const width = image.width || 1500;
      const parts = [];
      if (width >= 350) parts.push(`${image.srcset350} 350w`);
      if (width >= 550) parts.push(`${image.srcset550} 550w`);
      if (width >= 750) parts.push(`${image.srcset750} 750w`);
      if (width >= 991) parts.push(`${image.srcset991} 991w`);
      if (width >= 1200) parts.push(`${image.srcset1200} 1200w`);
      if (width >= 1500) parts.push(`${image.srcset1500} 1500w`);
      parts.push(`${image.srcsetFull} ${width}w`);
      return parts.join(', ');
    };

    const buildMainSlide = (image, index, containerWidth, zoomType) => {
      const loadingAttrs =
        index === 0 ? ' preload="true" fetchpriority="high"' : ' loading="lazy"';

      return `
        <div class="swiper-slide d-flex align-items-center product--media" data-mediaID="${image.id}" data-variant-gallery-slide role="treeitem">
          <img
            srcset="${buildSrcset(image)}"
            src="${image.src}"
            ${loadingAttrs}
            data-variant-title="${image.alt || ''}"
            sizes="(min-width: ${containerWidth}) calc(${containerWidth} / 2), 100vw"
            alt="${image.alt || ''}"
            width="${image.width}"
            height="${image.height}"
            class="${this.getGalleryImageClass(zoomType)}"
            ${this.getGalleryLightboxAttrs(zoomType, { url: image.srcsetFull, width: image.width, alt: image.alt || '', index })}
          >
        </div>
      `;
    };

    const buildThumbSlide = (image, index) => `
      <div class="swiper-slide product-thumb--image" data-index="${index + 1}" data-mediaID="${image.id}" data-variant-gallery-slide role="treeitem">
        <div class="thumb-item overflow-hidden">
          <img class="img-fluid"
            src="${image.thumb}"
            srcset="${image.thumb}"
            sizes="250px"
            loading="lazy"
            width="${image.width}"
            height="${image.height}"
            alt="${image.alt || ''}"
          />
        </div>
      </div>
    `;

    const getThumbSlider = () => {
      const thumbWrap = document.querySelector('[data-thumbSlider]');
      return thumbWrap ? thumbWrap.querySelector('.swiper') : null;
    };

    const updateSwiperSlides = (swiper, slidesHtml) => {
      if (!swiper) return;
      swiper.removeAllSlides();
      slidesHtml.forEach((slideHtml) => {
        swiper.appendSlide(slideHtml);
      });
      swiper.update();
      swiper.slideTo(0, 0, false);
    };

    this._variantGalleryMap = variantGalleryMap;
    this._fallbackGalleryImages = fallbackImages;

    this.renderVariantGallery = (variantId, bundleData = null, options = {}) => {
      const key = String(variantId);
      const logPrefix = '[BundleGallery]';
      const gallerySlider = document.querySelector('[data-variant-gallery-slider]');

      if (!gallerySlider) {
        console.warn(`${logPrefix} aborted: swiper gallery not found`);
        return false;
      }

      console.log(`${logPrefix} renderVariantGallery called`, {
        variantId: key,
        galleryMode: 'swiper',
        hasBundle: !!bundleData?.image,
        force: options.force === true,
      });

      if (!variantId) {
        console.warn(`${logPrefix} aborted: missing variantId`);
        return false;
      }

      const images =
        variantGalleryMap[key] && variantGalleryMap[key].length
          ? variantGalleryMap[key]
          : fallbackImages;

      if (!images.length && !bundleData?.image) {
        console.warn(`${logPrefix} aborted: no variant images and no bundle image`, {
          variantId: key,
          variantImageCount: images.length,
        });
        return false;
      }

      const containerWidth = gallerySlider.dataset.containerWidth || '1200px';
      const zoomType = gallerySlider.dataset.zoomType || '';

      const buildBundleMainSlideForRender = (imageSrc, imageAlt) => {
        const normalizedSrc = normalizeImageUrl(imageSrc);
        return `
          <div class="swiper-slide d-flex align-items-center product--media" data-mediaID="custom-bundle-image" data-variant-gallery-slide data-custom-bundle-slide role="treeitem">
            <img
              src="${normalizedSrc}"
              srcset="${withBundleWidth(normalizedSrc, 350)} 350w,
                      ${withBundleWidth(normalizedSrc, 550)} 550w,
                      ${withBundleWidth(normalizedSrc, 750)} 750w,
                      ${normalizedSrc} 991w,
                      ${withBundleWidth(normalizedSrc, 1200)} 1200w"
              preload="true" fetchpriority="high"
              data-variant-title="${imageAlt || ''}"
              sizes="(min-width: ${containerWidth}) calc(${containerWidth} / 2), 100vw"
              alt="${imageAlt || ''}"
              class="${this.getGalleryImageClass(zoomType)}"
              ${this.getGalleryLightboxAttrs(zoomType, { url: normalizedSrc, alt: imageAlt || '', index: -1 })}
            >
          </div>
        `;
      };

      const buildBundleThumbSlideForRender = (imageSrc, imageAlt) => {
        const normalizedSrc = normalizeImageUrl(imageSrc);
        return `
          <div class="swiper-slide product-thumb--image" data-index="0" data-mediaID="custom-bundle-thumb" data-variant-gallery-slide data-custom-bundle-slide role="treeitem">
            <div class="thumb-item overflow-hidden">
              <img class="img-fluid"
                src="${withBundleWidth(normalizedSrc, 250)}"
                srcset="${withBundleWidth(normalizedSrc, 250)}"
                sizes="250px"
                loading="lazy"
                alt="${imageAlt || ''}"
              />
            </div>
          </div>
        `;
      };

      const forceUpdate = options.force === true;
      if (!forceUpdate && gallerySlider.dataset.meridianGalleryVariantId === key && !bundleData) {
        console.log(`${logPrefix} skipped: same variant already rendered without bundle`, { variantId: key });
        return false;
      }

      gallerySlider.dataset.meridianGalleryVariantId = key;

      const mainWrapper = gallerySlider.querySelector('[data-variant-gallery-main]');
      const thumbWrapper = document.querySelector('[data-variant-gallery-thumbs]');
      const thumbSliderWrap = document.querySelector('[data-thumbSlider]');

      if (!mainWrapper) {
        console.warn(`${logPrefix} aborted: main wrapper not found`);
        return false;
      }

      const mainSlides = [];
      const thumbSlides = [];

      if (bundleData?.image) {
        const bundleAlt = bundleData.image_alt || bundleData.subheading || '';
        mainSlides.push(buildBundleMainSlideForRender(bundleData.image, bundleAlt));
        thumbSlides.push(buildBundleThumbSlideForRender(bundleData.image, bundleAlt));
      }

      images.forEach((image, index) => {
        const slideIndex = mainSlides.length;
        mainSlides.push(buildMainSlide(image, slideIndex, containerWidth, zoomType));
        thumbSlides.push(buildThumbSlide(image, slideIndex));
      });

      if (thumbSliderWrap) {
        if (mainSlides.length <= 1) {
          thumbSliderWrap.classList.add('d-none');
          thumbSliderWrap.classList.remove('d-md-block');
        } else {
          thumbSliderWrap.classList.remove('d-none');
          thumbSliderWrap.classList.add('d-md-block');
        }
      }

      const mainSwiper = gallerySlider.querySelector('.swiper')?.swiper || window.productSlider;
      const thumbSwiper = getThumbSlider()?.swiper;
      const mainUsesSwiperApi = !!(mainSwiper && typeof mainSwiper.removeAllSlides === 'function');
      const thumbUsesSwiperApi = !!(thumbSwiper && typeof thumbSwiper.removeAllSlides === 'function');

      console.log(`${logPrefix} applying swiper slides`, {
        variantImageCount: images.length,
        totalMainSlides: mainSlides.length,
        totalThumbSlides: thumbSlides.length,
        mainUsesSwiperApi,
        thumbUsesSwiperApi,
        mainSwiperFound: !!mainSwiper,
        thumbSwiperFound: !!thumbSwiper,
        productSliderGlobal: !!window.productSlider,
      });

      if (mainUsesSwiperApi) {
        updateSwiperSlides(mainSwiper, mainSlides);
      } else {
        console.warn(`${logPrefix} main gallery fallback to innerHTML`);
        mainWrapper.innerHTML = mainSlides.join('');
      }

      if (thumbUsesSwiperApi) {
        updateSwiperSlides(thumbSwiper, thumbSlides);
      } else if (thumbWrapper) {
        console.warn(`${logPrefix} thumb gallery fallback to innerHTML`);
        thumbWrapper.innerHTML = thumbSlides.join('');
      }

      const bundleSlideInDom = !!document.querySelector('[data-custom-bundle-slide]');
      const mainSlideCountAfter = mainSwiper?.slides?.length || mainWrapper.querySelectorAll('.swiper-slide').length;
      const firstSlideIsBundle = !!(
        mainSwiper?.slides?.[0]?.hasAttribute('data-custom-bundle-slide') ||
        mainWrapper.querySelector('.swiper-slide')?.hasAttribute('data-custom-bundle-slide')
      );

      console.log(`${logPrefix} swiper render complete`, {
        bundleSlideInDom,
        mainSlideCountAfter,
        firstSlideIsBundle,
        activeIndex: mainSwiper?.activeIndex ?? null,
      });

      if (bundleData?.image) {
        this.refreshSlider();
      } else {
        this.resetStainGalleryView();
      }

      return true;
    };

    const updateVariantGallery = (variantId) => {
      if (!document.querySelector('[data-variant-gallery-slider]')) return;
      this.renderVariantGallery(variantId);
    };

    const hasRenderableGallery = !!document.querySelector('[data-variant-gallery-slider]');

    console.log('[BundleGallery] initGallery ready', {
      hasRenderableGallery,
      renderVariantGalleryReady: typeof this.renderVariantGallery === 'function',
      applyBundleToGalleryReady: typeof this.applyBundleToGallery === 'function',
    });

    if (hasRenderableGallery) {
      this.bindVariantChange(updateVariantGallery);
    }
  }

  refreshBazaarvoiceWidget(host, config) {
    if (!host) return;

    const {
      rootAttribute,
      bvShow,
      productId,
      redirectUrl,
      variantId,
      variantDatasetKey,
    } = config;

    const currentRoot = host.querySelector(`[${rootAttribute}]`);
    const variantKey = String(variantId);

    if (
      currentRoot &&
      currentRoot.getAttribute('data-bv-product-id') === productId &&
      currentRoot.dataset[variantDatasetKey] === variantKey
    ) {
      return;
    }

    if (
      currentRoot &&
      currentRoot.getAttribute('data-bv-product-id') === productId &&
      !currentRoot.dataset[variantDatasetKey]
    ) {
      currentRoot.dataset[variantDatasetKey] = variantKey;
      return;
    }

    const root = document.createElement('div');
    root.setAttribute(rootAttribute, '');
    root.setAttribute('data-bv-show', bvShow);
    root.setAttribute('data-bv-product-id', productId);
    root.setAttribute('data-bv-seo', 'false');
    if (redirectUrl) {
      root.setAttribute('data-bv-redirect-url', redirectUrl);
    }
    root.dataset[variantDatasetKey] = variantKey;

    host.replaceChildren(root);
  }

  initVariantRatingSummary() {
    const ratingDataEl = document.querySelector('[data-variant-rating-summary-data]');
    if (!ratingDataEl) return;

    const ratingMap = JSON.parse(ratingDataEl.textContent);

    const updateVariantRatingSummary = (variantId) => {
      const entry = ratingMap[String(variantId)];
      if (!entry) return;

      this.refreshBazaarvoiceWidget(document.querySelector('[data-variant-rating-summary-host]'), {
        rootAttribute: 'data-variant-rating-summary-root',
        bvShow: 'rating_summary',
        productId: String(entry.productId ?? entry),
        redirectUrl: entry.redirectUrl || '',
        variantId,
        variantDatasetKey: 'meridianRatingVariantId',
      });
    };

    this.bindVariantChange(updateVariantRatingSummary);
  }

  initVariantProductTitle() {
    const titleDataEl = document.querySelector('[data-variant-product-title-data]');
    const titleTextEl = document.querySelector('[data-variant-product-title-text]');
    if (!titleDataEl || !titleTextEl) return;

    const titleMap = JSON.parse(titleDataEl.textContent);

    const updateVariantProductTitle = (variantId) => {
      const nextTitle = titleMap[String(variantId)];
      if (!nextTitle) return;

      if (titleTextEl.textContent !== nextTitle) {
        titleTextEl.textContent = nextTitle;
      }
    };

    this.bindVariantChange(updateVariantProductTitle);
  }

  initVariantBazaarvoiceReviews() {
    const reviewsDataEl = document.querySelector('[data-variant-bazaarvoice-reviews-data]');
    if (!reviewsDataEl) return;

    const reviewsMap = JSON.parse(reviewsDataEl.textContent);

    const updateVariantBazaarvoiceReviews = (variantId) => {
      const entry = reviewsMap[String(variantId)];
      if (!entry) return;

      const productId = String(entry.productId ?? entry);
      const redirectUrl = entry.redirectUrl || '';

      this.refreshBazaarvoiceWidget(document.querySelector('[data-variant-bv-reviews-host]'), {
        rootAttribute: 'data-variant-bv-reviews-root',
        bvShow: 'reviews',
        productId,
        redirectUrl,
        variantId,
        variantDatasetKey: 'meridianBvReviewsVariantId',
      });

      this.refreshBazaarvoiceWidget(document.querySelector('[data-variant-bv-questions-host]'), {
        rootAttribute: 'data-variant-bv-questions-root',
        bvShow: 'questions',
        productId,
        redirectUrl,
        variantId,
        variantDatasetKey: 'meridianBvQuestionsVariantId',
      });
    };

    this.bindVariantChange(updateVariantBazaarvoiceReviews);
  }

  initVariantRelatedProducts() {
    const relatedDataEl = document.querySelector('[data-variant-related-products-data]');
    if (!relatedDataEl) return;

    const relatedMap = JSON.parse(relatedDataEl.textContent);
    const root = document.querySelector('[data-variant-related-products-root]');
    const content = document.querySelector('[data-variant-related-products-content]');
    if (!root || !content) return;

    const updateVariantRelatedProducts = (variantId) => {
      const entry = relatedMap[String(variantId)];
      const variantKey = String(variantId);
      const nextHtml = entry?.html || '';

      if (!entry || !nextHtml.trim()) {
        root.hidden = true;
        content.innerHTML = '';
        delete content.dataset.meridianRelatedVariantId;
        return;
      }

      root.hidden = false;

      if (content.dataset.meridianRelatedVariantId === variantKey) {
        return;
      }

      if (!content.dataset.meridianRelatedVariantId && content.querySelector('slider-element')) {
        content.dataset.meridianRelatedVariantId = variantKey;
        return;
      }

      content.innerHTML = nextHtml;
      content.dataset.meridianRelatedVariantId = variantKey;
    };

    this.bindVariantChange(updateVariantRelatedProducts);
  }

  initVariantQrImage() {
    const qrDataEl = document.querySelector('[data-variant-qr-data]');
    const qrRoot = document.querySelector('[data-variant-qr-root]');
    const qrWrap = document.querySelector('[data-varinat-qr]');
    if (!qrDataEl || !qrWrap) return;

    let qrMap = {};
    try {
      qrMap = JSON.parse(qrDataEl.textContent);
    } catch {
      return;
    }

    const updateVariantQrImage = (variantId) => {
      const entry = qrMap[String(variantId)];
      const src = entry?.src || null;
      let img = qrWrap.querySelector('img');

      if (qrRoot) {
        qrRoot.classList.toggle('d-none', !src);
      }

      if (!src) {
        if (img) img.remove();
        return;
      }

      if (img) {
        if (img.getAttribute('src') !== src) {
          img.setAttribute('src', src);
        }
        return;
      }

      img = document.createElement('img');
      img.src = src;
      img.loading = 'lazy';
      img.alt = 'QR code';
      img.title = 'QR code';
      img.width = 90;
      img.height = 90;
      qrWrap.appendChild(img);
    };

    this.bindVariantChange(updateVariantQrImage);
  }

  initVariantFeatures() {
    const descriptionsEl = document.querySelector('[data-variant-short-descriptions]');
    if (!descriptionsEl) return;

    const descriptionMap = JSON.parse(descriptionsEl.textContent);

    const initReadMore = (root) => {
      const btn = root.querySelector('[data-product-features-read-more]');
      const container = root.querySelector('[data-variant-feature-content]');
      if (!btn || !container) return;

      const list = container.querySelector('ul');
      if (!list) {
        btn.hidden = true;
        return;
      }

      const items = list.querySelectorAll('li');
      if (items.length <= 4) {
        btn.hidden = true;
        items.forEach((item) => {
          item.style.display = 'list-item';
        });
        return;
      }

      items.forEach((item, index) => {
        item.style.display = index >= 4 ? 'none' : 'list-item';
      });
      btn.hidden = false;
      btn.onclick = () => {
        items.forEach((item) => {
          item.style.display = 'list-item';
        });
        btn.hidden = true;
      };
    };

    const updateVariantFeature = (variantId) => {
      const root = document.querySelector('[data-variant-feature-root]');
      if (!root) return;

      const html = descriptionMap[String(variantId)] || '';
      const container = root.querySelector('[data-variant-feature-content]');
      const btn = root.querySelector('[data-product-features-read-more]');

      if (!html || !container) {
        root.hidden = true;
        container.innerHTML = '';
        if (btn) btn.hidden = true;
        return;
      }

      root.hidden = false;
      container.innerHTML = html;
      initReadMore(root);
    };

    this.bindVariantChange(updateVariantFeature);
  }

  initVariantStains() {
    const stainsDataEl = document.querySelector('[data-variant-stains-data]');
    if (!stainsDataEl) return;

    const stainsMap = JSON.parse(stainsDataEl.textContent);

    const updateStainSummary = (root) => {
      if (!root || typeof Utility === 'undefined' || !window.globalVariables) return;

      const placeholder = root.querySelector('[data-stain-summary-placeholder]');
      const line = root.querySelector('[data-stain-summary-line]');
      const nameEl = root.querySelector('[data-stain-summary-name]');
      const compareEl = root.querySelector('[data-stain-summary-compare]');
      const priceEl = root.querySelector('[data-stain-summary-price]');
      if (!line || !nameEl || !priceEl) return;

      const checked = root.querySelector('input[data-addon-checkbox][data-stain-id]:checked');
      if (!checked) {
        if (placeholder) placeholder.classList.remove('d-none');
        line.classList.add('d-none');
        return;
      }

      if (placeholder) placeholder.classList.add('d-none');
      line.classList.remove('d-none');

      const displayName = checked.getAttribute('data-stain-display-name');
      if (displayName) nameEl.textContent = displayName;

      const totalCents = parseInt(checked.getAttribute('data-stain-total-cents'), 10) || 0;
      const compareCentsRaw = checked.getAttribute('data-stain-compare-total-cents');
      const compareCents = compareCentsRaw ? parseInt(compareCentsRaw, 10) : 0;
      const fmt = (cents) => Utility.formatMoney(cents, window.globalVariables.money_format);

      priceEl.textContent = fmt(totalCents);

      if (compareEl) {
        if (compareCents > totalCents) {
          compareEl.textContent = fmt(compareCents);
          compareEl.classList.remove('d-none');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('d-none');
        }
      }
    };

    const updateVariantStains = (variantId) => {
      const root = document.querySelector('[data-variant-stains-root]');
      const content = document.querySelector('[data-variant-stains-content]');
      if (!root || !content) return;
      // variant:changed also fires without a variant change (e.g. product-form init); keep selections and gallery position.
      if (root.dataset.renderedVariantId === String(variantId)) return;
      root.dataset.renderedVariantId = String(variantId);

      const data = stainsMap[String(variantId)];
      if (!data || !data.show) {
        root.hidden = true;
        content.innerHTML = '';
        this.triggerAddonPricingUpdate();
        return;
      }

      root.hidden = false;
      content.innerHTML = data.html;

      const summaryRoot = content.closest('[data-stain-summary-root]') || content;
      updateStainSummary(summaryRoot);
      this.openCollapsiblePanel(root);
      this.resetStainGalleryView();
      this.triggerAddonPricingUpdate();
    };

    this.container.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.matches('input[data-addon-checkbox][data-stain-id], input[data-stain-id]')) return;

      const summaryRoot = event.target.closest('[data-stain-summary-root]');
      if (event.target.checked && summaryRoot) {
        summaryRoot.querySelectorAll('[data-stain-id]').forEach((checkbox) => {
          if (checkbox !== event.target) checkbox.checked = false;
        });
      }

      updateStainSummary(summaryRoot);
      this.updateMainSliderForAddon(event);
      this.triggerAddonPricingUpdate(event.target);
      // Stains re-rendered on variant change are not bound by template-product.js; pass the real selection.
      window.__templateProductJS?._syncAccessoryGallerySlides?.(event.target);
    });

    this.bindVariantChange(updateVariantStains);
  }

  shouldSkipMainSliderUpdate(target) {
    if (!(target instanceof Element)) return false;
    if (target.hasAttribute('data-installation-checkbox')) return true;
    if (target.closest('.product-buy-together[data-buy-together-block-id]')) return true;
    if (target.hasAttribute('data-bt-base-price-cents')) return true;
    return false;
  }

  updateMainSliderForAddon(event) {
    const target = event?.target instanceof Element ? event.target : null;
    if (this.shouldSkipMainSliderUpdate(target)) return;

    if (document.querySelector('[data-variant-gallery-slider]') && target?.matches('[data-stain-id]')) {
      this.updateVariantGalleryForStain(target);
    }
  }

  updateSliderForStain(event) {
    this.updateMainSliderForAddon(event);
  }

  getStainMatchTokens(stainInput) {
    if (!stainInput) return [];

    const tokens = new Set();
    const addTokens = (value) => {
      String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .split(' ')
        .map((part) => part.trim())
        .filter((part) => part.length > 2)
        .forEach((part) => tokens.add(part));
    };

    addTokens(stainInput.getAttribute('data-stain-feature'));
    addTokens(stainInput.getAttribute('data-stain-display-name'));
    addTokens(stainInput.getAttribute('name'));

    return [...tokens];
  }

  findStainGallerySlideIndex(slides, stainInput) {
    if (!stainInput?.checked) return 0;

    const stainFeature = (stainInput.getAttribute('data-stain-feature') || '').toLowerCase();
    const stainName = (stainInput.getAttribute('data-stain-display-name') || '').toLowerCase();
    const stainHandle = (stainInput.getAttribute('name') || '').toLowerCase();
    const stainTokens = this.getStainMatchTokens(stainInput);

    let matchIndex = -1;

    slides.forEach((slide, index) => {
      if (matchIndex !== -1) return;

      const image = slide.querySelector('img');
      const title = (
        image?.getAttribute('data-variant-title') ||
        image?.getAttribute('alt') ||
        ''
      ).toLowerCase();

      if (!title) return;

      if (stainFeature && title.includes(stainFeature)) {
        matchIndex = index;
        return;
      }

      if (stainName && title.includes(stainName)) {
        matchIndex = index;
        return;
      }

      if (stainHandle && title.includes(stainHandle)) {
        matchIndex = index;
        return;
      }

      if (stainTokens.some((token) => title.includes(token))) {
        matchIndex = index;
      }
    });

    return matchIndex;
  }

  getMainGallerySwiper() {
    const gallerySlider = document.querySelector('[data-variant-gallery-slider]');
    if (!gallerySlider) return null;
    return gallerySlider.querySelector('.swiper')?.swiper || window.productSlider || null;
  }

  getThumbGallerySwiper() {
    const thumbSliderWrap = document.querySelector('[data-thumbSlider]');
    if (!thumbSliderWrap) return null;
    return thumbSliderWrap.querySelector('.swiper')?.swiper || null;
  }

  updateVariantGalleryForStain(stainInput) {
    const swiper = this.getMainGallerySwiper();
    if (!swiper) return;

    const gallerySlider = document.querySelector('[data-variant-gallery-slider]');
    const slides = gallerySlider?.querySelectorAll('.product--media') || [];

    if (!stainInput?.matches('[data-stain-id]') || !stainInput.checked) {
      swiper.slideTo(0, 0, false);
      this.syncGalleryLightbox();
      return;
    }

    const matchIndex = this.findStainGallerySlideIndex(slides, stainInput);
    swiper.slideTo(matchIndex >= 0 ? matchIndex : 0, 0, false);
    this.syncGalleryLightbox();
  }

  resetStainGalleryView() {
    const swiper = this.getMainGallerySwiper();
    if (swiper) {
      swiper.slideTo(0, 0, false);
    }
    this.syncGalleryLightbox();
  }

  triggerAddonPricingUpdate(eventTarget) {
    const target =
      eventTarget instanceof Element
        ? eventTarget
        : this.container.querySelector('[data-installation-checkbox]') ||
          this.container.querySelector('[data-addon-checkbox]') ||
          document.body;

    if (window.__templateProductJS?._checkAddon) {
      window.__templateProductJS._checkAddon({ currentTarget: target });
      window.__templateProductJS._updateAccessoriesWoobtSaved?.();
      window.__templateProductJS._applyBuyTogetherTierPricing?.();
      window.__templateProductJS._updateBuyTogetherSelectedCount?.();
      this.clearMeridianBuyTogetherDNone();
      return;
    }

    this.recalculateCartTotal();
    this.clearMeridianBuyTogetherDNone();
  }

  initMeridianBuyTogether() {
    this.patchMeridianBuyTogetherVisibility();
    // template-product.js is lazy-loaded via data-src; retry the hide-card patch when it arrives.
    if (!window.__templateProductJS && !this._meridianBuyTogetherPatchRetryBound) {
      this._meridianBuyTogetherPatchRetryBound = true;
      window.addEventListener('template-product:ready', () => {
        this.patchMeridianBuyTogetherVisibility();
        this.clearMeridianBuyTogetherDNone();
      }, { once: true });
    }

    const run = () => this.clearMeridianBuyTogetherDNone();

    this.container.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.matches('[data-addon-checkbox]')) return;
      if (!event.target.closest('.product-buy-together[data-buy-together-block-id]')) return;

      const checkbox = event.target;
      const card = checkbox.closest('.buy-together-card[data-accessories-wrap]');
      const intendedChecked = checkbox.checked;

      requestAnimationFrame(() => {
        // Pricing/visibility helpers can clear `checked` when the product JS patch
        // is not ready yet — restore the user's intended state after they run.
        this.triggerAddonPricingUpdate(checkbox);
        this.clearMeridianBuyTogetherDNone();
        if (checkbox instanceof HTMLInputElement) {
          checkbox.checked = intendedChecked;
        }
        if (card) {
          card.classList.toggle('choose-selected', intendedChecked);
        }
        this.updatePackageSelection();
      });
    });

    document.addEventListener('buy-together:variant-updated', (event) => {
      const panel = this.container.querySelector('.product-buy-together[data-buy-together-block-id]');
      if (panel) {
        if (event.detail?.empty) {
          panel.classList.remove('open');
          panel.querySelector('.toggle-content')?.classList.remove('open');
          panel.querySelector('.panel_toggle')?.setAttribute('aria-expanded', 'false');
        } else {
          this.openCollapsiblePanel(panel);
          this.initQuickViewFeaturesReadMore(panel);
        }
      }
      this.clearMeridianBuyTogetherDNone();
      this.triggerAddonPricingUpdate();
      this.updatePackageSelection();
    });

    run();
    window.requestAnimationFrame(run);
    window.addEventListener('load', run, { once: true });
  }

  isMeridianBuyTogetherCard(card) {
    return (
      card instanceof Element &&
      this.container?.contains(card) &&
      card.matches('.buy-together-card[data-accessories-wrap]') &&
      !!card.closest('.product-buy-together[data-buy-together-block-id]')
    );
  }

  patchMeridianBuyTogetherVisibility() {
    const js = window.__templateProductJS;
    if (!js || js._meridianBuyTogetherVisibilityPatched) return;

    js._meridianBuyTogetherVisibilityPatched = true;

    const originalHideCard = js._hideBuyTogetherCard.bind(js);
    js._hideBuyTogetherCard = (card) => {
      if (this.isMeridianBuyTogetherCard(card)) {
        return false;
      }
      return originalHideCard(card);
    };

    const originalHidePanel = js._hideBuyTogetherPanel.bind(js);
    js._hideBuyTogetherPanel = (panel) => {
      if (panel instanceof Element && this.container?.contains(panel)) {
        if (panel.hasAttribute('data-buy-together-empty')) return false;
        panel.classList.remove('d-none');
        panel.querySelectorAll('.buy-together-card[data-accessories-wrap]').forEach((card) => {
          card.classList.remove('d-none');
          card.closest('.swiper-slide')?.classList.remove('d-none');
          card.querySelector('[data-addon-checkbox]')?.classList.remove('d-none');
        });
        window.__templateProductJS?._refreshBuyTogetherSlides?.();
        return false;
      }
      return originalHidePanel(panel);
    };

    const originalRefreshSlides = js._refreshBuyTogetherSlides.bind(js);
    js._refreshBuyTogetherSlides = () => {
      this.clearMeridianBuyTogetherDNone();
      originalRefreshSlides();
      this.clearMeridianBuyTogetherDNone();
    };
  }

  clearMeridianBuyTogetherDNone() {
    if (!this.container) return;

    this.container.querySelectorAll('.product-buy-together[data-buy-together-block-id]').forEach((panel) => {
      if (panel.hasAttribute('data-buy-together-empty') || panel.hasAttribute('hidden')) return;

      panel.classList.remove('d-none');

      panel.querySelectorAll('.buy-together-card[data-accessories-wrap]').forEach((card) => {
        const checkbox = card.querySelector('[data-addon-checkbox]');
        if (!checkbox) return;
        card.classList.remove('d-none');
        card.closest('.swiper-slide')?.classList.remove('d-none');
        checkbox.classList.remove('d-none');
      });
    });
  }

  recalculateCartTotal() {
    if (!window.globalVariables?.product?.currentVariant) return;

    const productPrice = window.globalVariables.product.currentVariant.price / 100;
    let total = productPrice;
    let installationBase = null;

    const stainsRoot = document.querySelector('[data-variant-stains-root]');
    if (stainsRoot) {
      stainsRoot.querySelectorAll('input[data-addon-checkbox][data-stain-id]:checked').forEach((inp) => {
        const cents = parseInt(inp.getAttribute('data-stain-total-cents'), 10) || 0;
        total += cents / 100;
      });
    }

    this.container.querySelectorAll('[data-addon-checkbox]:checked:not([data-stain-id])').forEach((ele) => {
      if (ele.hasAttribute('data-installation-checkbox')) {
        const inst = parseFloat(String(ele.getAttribute('data-intallation-price') || '').replace(/,/g, ''));
        if (!Number.isNaN(inst)) installationBase = inst;
        return;
      }

      let addonPrice = NaN;
      if (ele.hasAttribute('data-bt-base-price-cents')) {
        const card = ele.closest('.buy-together-card[data-accessories-wrap]');
        const renderedPriceEl = card?.querySelector('[data-currentprice] [data-accessory-price]');
        if (renderedPriceEl) {
          addonPrice = parseFloat((renderedPriceEl.textContent || '').replace(/[^0-9.-]/g, ''));
        }
      }

      if (Number.isNaN(addonPrice)) {
        const priceAttr =
          ele.getAttribute('data-accessory-price') ||
          ele.getAttribute('data-installation-price') ||
          ele.getAttribute('data-addon-price') ||
          ele.getAttribute('data_assembly_price');
        if (priceAttr) {
          addonPrice = parseFloat(String(priceAttr).replace(/,/g, ''));
        }
      }

      if (!Number.isNaN(addonPrice)) total += addonPrice;
    });

    if (installationBase != null) {
      total = installationBase + (total - productPrice);
    }

    const formatted =
      typeof Utility !== 'undefined' && Utility.formatMoney
        ? Utility.formatMoney(total * 100, window.globalVariables.money_format)
        : '$' + total.toFixed(2);

    document.querySelectorAll('[cart-total]').forEach((el) => {
      el.textContent = formatted;
    });
  }

  initVariantMainProductCommerce() {
    const dataEl = document.querySelector('[data-variant-main-product-commerce]');
    if (!dataEl) return;

    let commerceMap = {};
    try {
      commerceMap = JSON.parse(dataEl.textContent);
    } catch {
      return;
    }

    this._mainProductCommerceMap = commerceMap;

    const syncCommerce = (bundleVariantId) => {
      // Run after VariantSelects updateURLandID / priceUpdate so Meridian wins.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.applyMainProductCommerce(bundleVariantId);
          this.triggerAddonPricingUpdate();
          this.updateProductTabsStickyForm();
        });
      });
    };

    this.bindVariantChange(syncCommerce);
  }

  getMainProductCommerceEntry(bundleVariantId) {
    if (!bundleVariantId || !this._mainProductCommerceMap) return null;
    return this._mainProductCommerceMap[String(bundleVariantId)] || null;
  }

  getSelectedBundleVariant(bundleVariantId) {
    const variantId = bundleVariantId || window.globalVariables?.product?.currentVariant?.id;
    if (!variantId) return null;

    const variantJsonEl = document.querySelector('[data-variantJSON]');
    if (variantJsonEl) {
      try {
        const variants = JSON.parse(variantJsonEl.textContent);
        const match = variants.find((variant) => String(variant.id) === String(variantId));
        if (match) return match;
      } catch {
        /* use the live selected variant below */
      }
    }

    const currentVariant = window.globalVariables?.product?.currentVariant;
    if (currentVariant && String(currentVariant.id) === String(variantId)) return currentVariant;
    return null;
  }

  applyMainProductCommerce(bundleVariantId) {
    const entry = this.getMainProductCommerceEntry(bundleVariantId);
    if (!entry?.variantId) return;

    if (window.globalVariables?.product) {
      window.globalVariables.product.meridianMainProduct = entry;
      const currentVariant = window.globalVariables.product.currentVariant;
      if (currentVariant) {
        // Keep the selected bundle variant's price. Linked-product commerce
        // only drives availability and the notify-me target.
        currentVariant.available = entry.available;
        currentVariant.inventory_quantity = entry.inventoryQuantity;
        currentVariant.meridianCommerceVariantId = entry.variantId;
      }
    }

    this.syncMainProductFormInputs(entry, bundleVariantId);
    this.syncMainProductPriceDisplay(bundleVariantId);
    this.syncMainProductAvailability(entry);
    this.syncMainProductKlaviyoNotify(entry);
  }

  syncMainProductFormInputs(entry, bundleVariantId) {
    const idInputs = document.querySelectorAll(
      [
        '.template-meridian-gazebo-bundle product-form form input[name="id"]',
        '[data-variant-tabs-sticky-form] input[name="id"]',
        '[data-meridian-sticky-form] input[name="id"]'
      ].join(', ')
    );

    idInputs.forEach((input) => {
      const selectedVariantId = bundleVariantId || input.getAttribute('data-bundle-variant-id');
      if (!selectedVariantId) return;

      input.value = String(selectedVariantId);
      input.setAttribute('data-variant-id', String(selectedVariantId));
      input.setAttribute('data-bundle-variant-id', String(selectedVariantId));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const handleInputs = document.querySelectorAll(
      [
        '.template-meridian-gazebo-bundle [data-assembly-element]',
        '[data-variant-tabs-sticky-form] [data-assembly-property]',
        '[data-meridian-sticky-form] [data-assembly-property]'
      ].join(', ')
    );

    handleInputs.forEach((el) => {
      el.setAttribute('data-product_handle', entry.productHandle);
      if (el.getAttribute('name') === 'properties[_main_product_handle]') {
        el.value = entry.productHandle;
      }
    });
  }

  syncMainProductKlaviyoNotify(entry) {
    const available = entry.available === true;
    const triggers = document.querySelectorAll(
      [
        '.template-meridian-gazebo-bundle [data-meridian-klaviyo-bis]',
        '.template-meridian-gazebo-bundle .klaviyo-bis-trigger'
      ].join(', ')
    );

    triggers.forEach((trigger) => {
      trigger.setAttribute('data-product-id', String(entry.productId));
      trigger.setAttribute('data-variant-id', String(entry.variantId));
      if (entry.productTitle) trigger.setAttribute('data-product-name', entry.productTitle);
      if (entry.productUrl) trigger.setAttribute('data-product-url', entry.productUrl);
      if (entry.image) trigger.setAttribute('data-product-image', entry.image);
      if (entry.sku != null) trigger.setAttribute('data-sku', String(entry.sku));
      trigger.classList.toggle('d-none', available);
      trigger.hidden = available;
      trigger.style.display = available ? 'none' : '';
    });

    // Keep Shopify/Klaviyo page meta in sync with the linked sellable product.
    if (window.ShopifyAnalytics?.meta) {
      if (window.ShopifyAnalytics.meta.product) {
        window.ShopifyAnalytics.meta.product.id = entry.productId;
      }
      window.ShopifyAnalytics.meta.selectedVariantId = entry.variantId;
    }

    if (window.meta?.product) {
      window.meta.product.id = entry.productId;
    }

    // Re-init BIS so the notify modal targets the newly selected linked product/variant.
    window.setTimeout(() => {
      try {
        if (typeof window._klaviyo?.init === 'function') {
          window._klaviyo.init();
        } else if (Array.isArray(window.klaviyo) && typeof window.klaviyo.enable === 'function') {
          // already enabled via snippet; variant attrs on trigger/forms are enough
        }
      } catch {
        /* Klaviyo onsite may not expose init in actualizar versions */
      }
    }, 0);
  }

  syncMainProductPriceDisplay(bundleVariantId) {
    if (typeof Utility === 'undefined' || !window.globalVariables?.money_format) return;

    const selectedVariant = this.getSelectedBundleVariant(bundleVariantId);
    if (!selectedVariant || selectedVariant.price == null) return;

    const price = selectedVariant.price;
    const compareAtPrice = selectedVariant.compare_at_price;
    const priceText = Utility.formatMoney(price, window.globalVariables.money_format);
    const compareText =
      compareAtPrice != null
        ? Utility.formatMoney(compareAtPrice, window.globalVariables.money_format)
        : '';
    const hasCompare = compareAtPrice != null && compareAtPrice > price;

    document
      .querySelectorAll(
        [
          '.template-meridian-gazebo-bundle [data-meridian-price-root] [data-currentPrice]',
          '.template-meridian-gazebo-bundle [data-price-wrap-element] [data-currentPrice]'
        ].join(', ')
      )
      .forEach((el) => {
        // Leave cart-total nodes for _checkAddon / recalculateCartTotal.
        if (el.hasAttribute('cart-total')) return;
        el.textContent = priceText;
      });

    document
      .querySelectorAll(
        [
          '.template-meridian-gazebo-bundle [data-meridian-price-root] [data-comparePrice]',
          '.template-meridian-gazebo-bundle [data-price-wrap-element] [data-comparePrice]'
        ].join(', ')
      )
      .forEach((el) => {
        if (compareText) el.textContent = compareText;
        el.classList.toggle('d-none', !hasCompare);
      });

    // Base the top bundle total on the selected variant. Buy-together add-ons
    // are added on top of this in template-product.js (_updateBundleTopPrice).
    document.querySelectorAll('.template-meridian-gazebo-bundle [data-meridian-price-root]').forEach((el) => {
      el.setAttribute('data-bundle-base-price-cents', String(price));
      el.setAttribute(
        'data-bundle-base-compare-cents',
        String(compareAtPrice != null ? compareAtPrice : price)
      );
    });

    document.querySelectorAll('.affirm-as-low-as, [data-affrim-widget]').forEach((el) => {
      el.setAttribute('data-amount', String(price));
    });
  }

  syncMainProductAvailability(entry) {
    const available = entry.available === true;
    const addToCartLabel =
      window.variantStrings?.productAddToCart || window.variantStrings?.addToCart || 'Add to Cart';
    const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';

    const buttons = document.querySelectorAll(
      [
        '.template-meridian-gazebo-bundle product-form [type="submit"][name="add"]',
        '[data-variant-tabs-sticky-form] [type="submit"][name="add"]',
        '[data-meridian-sticky-form] [type="submit"][name="add"]'
      ].join(', ')
    );

    buttons.forEach((btn) => {
      const labelEl =
        btn.querySelector('.add-text:not([cart-total])') || btn.querySelector('.add-text');

      if (available) {
        btn.removeAttribute('disabled');
        btn.classList.remove('d-none');
        btn.hidden = false;
        if (labelEl && !labelEl.hasAttribute('cart-total')) {
          const needsBullet = btn.hasAttribute('data-atc-sticky-btn') || btn.closest('[data-variant-tabs-sticky-form]');
          labelEl.textContent = needsBullet ? `${addToCartLabel} • ` : addToCartLabel;
        }
      } else {
        btn.setAttribute('disabled', 'disabled');
        // Keep ATC in DOM for layout; Klaviyo notify is shown beside it when OOS.
        if (labelEl && !labelEl.hasAttribute('cart-total')) {
          labelEl.textContent = soldOutLabel;
        }
      }
    });
  }

  initVariantInstallation() {
    this._installationZipValidationId = 0;

    const installationDataEl = document.querySelector('[data-variant-installation-data]');
    if (!installationDataEl) return;

    const installationMap = JSON.parse(installationDataEl.textContent);
    const root = document.querySelector('[data-variant-installation-root]');
    const content = document.querySelector('[data-variant-installation-content]');
    const discountEl = document.querySelector('[data-variant-installation-discount]');
    const installationIdInput = document.querySelector('[data-variant-installation-id]');

    const resetInstallationState = () => {
      this._installationZipValidationId += 1;
      if (window.globalVariables) {
        window.globalVariables.installation_available = false;
        window.globalVariables.zipcode = '';
      }
    };

    const updateAssemblyHandle = (handle) => {
      document.querySelectorAll('[data-assembly-property]').forEach((el) => {
        if (handle) {
          el.setAttribute('data-product_assembly_handle', handle);
        } else {
          el.removeAttribute('data-product_assembly_handle');
        }
      });
    };

    const updateVariantInstallation = (variantId) => {
      if (!root || !content) return;

      const data = installationMap[String(variantId)];
      if (!data || !data.show) {
        root.hidden = true;
        content.innerHTML = '';
        if (discountEl) {
          discountEl.textContent = JSON.stringify({
            assembly_discount: null,
            product_discount: data?.product_discount ?? null
          });
        }
        if (installationIdInput) installationIdInput.removeAttribute('installation_id');
        updateAssemblyHandle(null);
        resetInstallationState();
        this.triggerAddonPricingUpdate();
        return;
      }

      root.hidden = false;
      content.innerHTML = data.html;
      this.openInstallationPanel(root);

      if (discountEl) {
        discountEl.textContent = JSON.stringify({
          assembly_discount: data.assembly_discount,
          product_discount: data.product_discount
        });
      }

      if (installationIdInput) {
        if (data.installation_variant_id) {
          installationIdInput.setAttribute('installation_id', data.installation_variant_id);
        } else {
          installationIdInput.removeAttribute('installation_id');
        }
      }

      updateAssemblyHandle(data.installation_handle);
      resetInstallationState();
      this.triggerAddonPricingUpdate();
    };

    this.container.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.matches('[data-installation-checkbox]')) return;

      this.triggerAddonPricingUpdate(event.target);
      this.syncInstallationZipUI(event.target.checked);
    });

    this.container.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const installationRoot = event.target.closest('[data-variant-installation-root]');
      if (!installationRoot || !this.container.contains(installationRoot)) return;

      const zipBtn = event.target.closest('[data-zip]');
      if (zipBtn && installationRoot.contains(zipBtn)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.validateInstallationZipcode({ currentTarget: zipBtn });
        return;
      }

      const resetBtn = event.target.closest('[data-zip-reset]');
      if (resetBtn && installationRoot.contains(resetBtn)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.resetInstallationZipcode(resetBtn);
      }
    }, true);

    this.container.addEventListener('keydown', (event) => {
      if (!(event.target instanceof Element) || !event.target.matches('[zip-value]')) return;
      if (event.key !== 'Enter') return;
      event.preventDefault();

      const scope = event.target.closest('.zipcode-content');
      const btn = scope?.querySelector('[data-zip]') || this.container.querySelector('[data-zip]');
      if (btn) this.validateInstallationZipcode({ currentTarget: btn });
    });

    this.bindVariantChange(updateVariantInstallation);
  }

  openCollapsiblePanel(panel) {
    if (!panel) return;

    panel.classList.add('open');
    panel.querySelector('.toggle-content')?.classList.add('open');
    const toggle = panel.querySelector('.panel_toggle');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
  }

  bindCollapsiblePanel(panel) {
    if (!panel) return;

    if (panel.hasAttribute('data-meridian-collapsible')) {
      return;
    }

    let toggle = panel.querySelector('.panel_toggle');
    if (!toggle) return;

    // Strip any listeners from collapsiblePanel.init() before attaching the Meridian handler.
    const freshToggle = toggle.cloneNode(true);
    freshToggle.dataset.meridianToggleBound = 'true';
    toggle.replaceWith(freshToggle);
    toggle = freshToggle;

    const handleToggle = (event) => {
      if (event.target.closest('[data-modal-opener]')) return;
      if (event.target.closest('#installCheck')) return;
      const innerLink = event.target.closest('a[href]');
      if (innerLink && innerLink !== toggle && toggle.contains(innerLink)) return;
      if (toggle.tagName === 'A') event.preventDefault();

      event.preventDefault();
      event.stopPropagation();

      if (panel.classList.contains('open')) {
        toggle.setAttribute('aria-expanded', 'false');
        panel.classList.remove('open');
        panel.querySelector('.toggle-content')?.classList.remove('open');
      } else {
        this.openCollapsiblePanel(panel);
      }
    };

    toggle.addEventListener('click', handleToggle);
    if (toggle.getAttribute('role') === 'button' && toggle.tagName !== 'BUTTON') {
      toggle.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleToggle(event);
        }
      });
    }
  }

  openInstallationPanel(panel) {
    this.openCollapsiblePanel(panel);
  }

  initVariantAccessories() {
    const accessoriesDataEl = document.querySelector('[data-variant-accessories-data]');
    if (!accessoriesDataEl) return;

    const accessoriesMap = JSON.parse(accessoriesDataEl.textContent);
    const root = document.querySelector('[data-variant-accessories-root]');
    const content = document.querySelector('[data-variant-accessories-content]');

    const updateVariantAccessories = (variantId) => {
      if (!root || !content) return;
      // variant:changed also fires without a variant change (e.g. product-form init); keep selections and gallery position.
      if (root.dataset.renderedVariantId === String(variantId)) return;
      root.dataset.renderedVariantId = String(variantId);

      const data = accessoriesMap[String(variantId)];
      if (!data || !data.show) {
        root.hidden = true;
        content.innerHTML = '';
        this.triggerAddonPricingUpdate();
        return;
      }

      root.hidden = false;
      content.innerHTML = data.html;

      const panel = content.querySelector('[data-compatible-accessories-panel]');
      this.openCollapsiblePanel(panel);
      this.initQuickViewFeaturesReadMore(content);
      this.resetStainGalleryView();
      this.triggerAddonPricingUpdate();
    };

    this.container.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.matches('[data-addon-checkbox]')) return;
      if (event.target.matches('[data-stain-id], [data-installation-checkbox]')) return;
      if (!event.target.closest('[data-variant-accessories-root]')) return;

      this.updateMainSliderForAddon(event);
      this.triggerAddonPricingUpdate(event.target);
      // Accessories re-rendered on variant change are not bound by template-product.js; pass the real selection.
      window.__templateProductJS?._syncAccessoryGallerySlides?.(event.target);
    });

    this.container.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;

      const accessoriesRoot = event.target.closest('[data-variant-accessories-root]');
      if (!accessoriesRoot) return;

      const card = event.target.closest('.buy-together-card[data-accessories-wrap]');
      if (card && accessoriesRoot.contains(card)) {
        if (event.target.closest('[data-modal-opener], .quick-view-button')) return;
        if (event.target.closest('.form-check, [data-addon-checkbox], label.form-check-label')) return;

        const checkbox = card.querySelector('[data-addon-checkbox]');
        if (checkbox && !checkbox.disabled) {
          event.preventDefault();
          checkbox.checked = !checkbox.checked;
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
    });

    this.bindVariantChange(updateVariantAccessories);
  }

  initVariantProductTabs() {
    const tabsDataEl = document.querySelector('[data-variant-product-tabs-data]');
    if (!tabsDataEl) return;

    const tabsMap = JSON.parse(tabsDataEl.textContent);
    const root = document.querySelector('[data-variant-product-tabs-root]');
    const nav = document.querySelector('[data-variant-product-tabs-nav]');
    const panels = document.querySelector('[data-variant-product-tabs-panels]');
    const customTabsEl = root?.querySelector('custom-tabs');

    if (customTabsEl) this.bindProductTabNavigation(customTabsEl);

    const updateVariantProductTabs = (variantId) => {
      if (!root || !nav || !panels) return;

      const data = tabsMap[String(variantId)];
      if (!data || !data.show) {
        root.hidden = true;
        nav.innerHTML = '';
        panels.innerHTML = '';
        return;
      }

      root.hidden = false;
      nav.innerHTML = data.navHtml || '';
      panels.innerHTML = data.panelsHtml || '';

      this.reinitProductTabPanels(panels);
      this.activateFirstProductTab(customTabsEl);
      this.openFirstProductTabCollapsible(panels);
      this.updateProductTabsStickyForm();
    };

    this.bindVariantChange(updateVariantProductTabs);
  }

  bindProductTabNavigation(customTabsEl) {
    if (!customTabsEl || customTabsEl.dataset.meridianProductTabsBound === 'true') return;
    customTabsEl.dataset.meridianProductTabsBound = 'true';

    customTabsEl.addEventListener('click', (event) => {
      const tabLink = event.target.closest('.tablink');
      if (!tabLink || !customTabsEl.contains(tabLink)) return;
      event.preventDefault();

      const toggle = tabLink.querySelector('[data-toggle="tab"]');
      const contentSelector = toggle?.dataset.tabcontent;
      const tabContainer = contentSelector ? customTabsEl.querySelector(contentSelector) : null;
      if (!toggle || !tabContainer) return;

      customTabsEl.querySelectorAll('.tabcontent').forEach((panel) => panel.classList.remove('open'));
      customTabsEl.querySelectorAll('.tablink').forEach((link) => {
        link.classList.remove('open');
        link.querySelector('[data-toggle="tab"]')?.setAttribute('aria-expanded', 'false');
      });

      tabLink.classList.add('open');
      toggle.setAttribute('aria-expanded', 'true');
      tabContainer.classList.add('open');
    });
  }

  activateFirstProductTab(customTabsEl) {
    if (!customTabsEl) return;

    const firstTabLink = customTabsEl.querySelector('.tabs .tablink');
    const firstToggle = firstTabLink?.querySelector('[data-toggle="tab"]');
    const firstContainer = firstToggle?.dataset.tabcontent
      ? customTabsEl.querySelector(firstToggle.dataset.tabcontent)
      : customTabsEl.querySelector('.tabcontent');

    customTabsEl.querySelectorAll('.tabcontent').forEach((panel) => panel.classList.remove('open'));
    customTabsEl.querySelectorAll('.tabs .tablink').forEach((link) => {
      link.classList.remove('open');
      link.querySelector('[data-toggle="tab"]')?.setAttribute('aria-expanded', 'false');
    });

    if (firstTabLink && firstContainer) {
      firstTabLink.classList.add('open');
      firstToggle?.setAttribute('aria-expanded', 'true');
      firstContainer.classList.add('open');
    }
  }

  reinitProductTabPanels(panelsRoot) {
    if (!panelsRoot) return;

    panelsRoot.querySelectorAll('[is="collapsiblePanel"]').forEach((panel) => {
      const toggle = panel.querySelector('.panel_toggle');
      if (toggle) delete toggle.dataset.meridianToggleBound;
      this.bindCollapsiblePanel(panel);
    });
  }

  openFirstProductTabCollapsible(panelsRoot) {
    const openTab =
      panelsRoot?.querySelector('.tabcontent.open') || panelsRoot?.querySelector('.tabcontent');
    const panel = openTab?.querySelector('[is="collapsiblePanel"]');
    if (panel) this.openCollapsiblePanel(panel);
  }

  updateProductTabsStickyForm() {
    const stickyForm = document.querySelector('[data-variant-tabs-sticky-form]');
    const currentVariant = window.globalVariables?.product?.currentVariant;
    if (!stickyForm || !currentVariant) return;

    const commerce =
      window.globalVariables?.product?.meridianMainProduct ||
      this.getMainProductCommerceEntry(this.getSelectedVariantId());
    const commerceVariantId = commerce?.variantId || currentVariant.meridianCommerceVariantId;
    const available = commerce ? commerce.available === true : currentVariant.available === true;

    const idInput = stickyForm.querySelector('input[name="id"]');
    if (idInput) idInput.value = commerceVariantId || currentVariant.id;

    const handleInput = stickyForm.querySelector('[data-assembly-property], [name="properties[_main_product_handle]"]');
    if (handleInput && commerce?.productHandle) {
      handleInput.setAttribute('data-product_handle', commerce.productHandle);
      if (handleInput.getAttribute('name') === 'properties[_main_product_handle]') {
        handleInput.value = commerce.productHandle;
      }
    }

    const submitBtn = stickyForm.querySelector('[type="submit"]');
    if (!submitBtn) return;

    const labelEl =
      submitBtn.querySelector('.add-text:not([cart-total])') || submitBtn.querySelector('.add-text');
    const addToCartLabel =
      window.variantStrings?.productAddToCart || window.variantStrings?.addToCart || 'Add to Cart';
    const soldOutLabel = window.variantStrings?.soldOut || 'Sold out';

    if (available) {
      submitBtn.removeAttribute('disabled');
      if (labelEl && !labelEl.hasAttribute('cart-total')) {
        labelEl.textContent = `${addToCartLabel} • `;
      }
    } else {
      submitBtn.setAttribute('disabled', 'disabled');
      if (labelEl && !labelEl.hasAttribute('cart-total')) {
        labelEl.textContent = soldOutLabel;
      }
    }
  }

  syncInstallationZipUI(isChecked) {
    const installationRoot = this.container.querySelector('[data-variant-installation-root]');
    const zipcodeSection = installationRoot?.querySelector('[data-zipcode-section]');
    const zipCodeBox = installationRoot?.querySelector('[data-zip-code-box]');

    if (this._zipcodeSectionHideTimer) {
      clearTimeout(this._zipcodeSectionHideTimer);
      this._zipcodeSectionHideTimer = null;
    }

    if (isChecked) {
      const installationConfirmed = window.globalVariables?.installation_available === true;

      if (zipcodeSection) {
        zipcodeSection.classList.remove('d-none');
        void zipcodeSection.offsetHeight;
        zipcodeSection.classList.add('active');
      }

      if (zipCodeBox) {
        if (installationConfirmed) {
          zipCodeBox.classList.remove('d-sm-flex', 'd-flex', 'd-md-flex', 'show-inline-transition', 'hide-inline-transition');
          zipCodeBox.classList.add('d-none');
        } else {
          zipCodeBox.classList.remove('d-none', 'hide-inline-transition');
          zipCodeBox.classList.add('d-sm-flex', 'show-inline-transition');
        }
      }

      if (!installationConfirmed && installationRoot) {
        installationRoot.querySelector('[data-installation-available]:not([data-addon-checkbox])')?.classList.add('d-none');
        installationRoot.querySelector('[data-installation-not-available]')?.classList.add('d-none');
        installationRoot.querySelector('[data-zip-reset]')?.classList.add('d-none');
      }
      return;
    }

    if (zipcodeSection) {
      zipcodeSection.classList.remove('active');
      this._zipcodeSectionHideTimer = setTimeout(() => {
        this._zipcodeSectionHideTimer = null;
        if (!zipcodeSection.classList.contains('active')) {
          zipcodeSection.classList.add('d-none');
        }
      }, 400);
    }

    if (zipCodeBox) {
      zipCodeBox.classList.remove('d-sm-flex', 'd-flex', 'd-md-flex', 'show-inline-transition', 'hide-inline-transition');
      zipCodeBox.classList.add('d-none');
    }

    installationRoot?.querySelector('[data-installation-available]:not([data-addon-checkbox])')?.classList.add('d-none');
    installationRoot?.querySelector('[data-installation-not-available]')?.classList.add('d-none');
    installationRoot?.querySelector('[data-zip-reset]')?.classList.add('d-none');

    if (window.globalVariables) {
      window.globalVariables.installation_available = false;
      window.globalVariables.zipcode = '';
    }
  }

  validateInstallationZipcode(event) {
    const currentBtn = event.currentTarget;
    const scope = currentBtn.closest('.zipcode-content');
    const zipInput = scope?.querySelector('[zip-value]') || this.container.querySelector('[zip-value]');
    const zipcode = zipInput ? zipInput.value.trim() : '';

    if (!zipcode) {
      if (window.notificationEle) {
        window.notificationEle.updateNotification('Zip Code', 'Please enter a zipcode', {
          type: 'error',
          timeout: 5000
        });
      }
      if (zipInput) zipInput.value = '';
      return;
    }

    currentBtn.setAttribute('disabled', 'true');
    currentBtn.classList.add('loading');

    if (window.globalVariables) {
      window.globalVariables.zipcode = zipcode;
    }

    const validationId = ++this._installationZipValidationId;

    fetch('/apps/zip-code-validator/api/distribution-center', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ postal_code: zipcode, brand: 'Yardistry' })
    })
      .then((response) => response.json())
      .then((response) => {
        if (validationId !== this._installationZipValidationId) return;
        const isAvailable = response && (response.status === true || response.status === 'true');
        this.handleInstallationZipcodeResult(isAvailable, scope);
      })
      .catch(() => {
        if (validationId !== this._installationZipValidationId) return;
        this.handleInstallationZipcodeResult(false, scope);
      })
      .finally(() => {
        currentBtn.classList.remove('loading');
        currentBtn.removeAttribute('disabled');
        if (zipInput) zipInput.value = '';
      });
  }

  handleInstallationZipcodeResult(isAvailable, scope) {
    const installationRoot = this.container.querySelector('[data-variant-installation-root]');
    const q = (sel) =>
      (scope && scope.querySelector(sel)) ||
      installationRoot?.querySelector(sel) ||
      this.container.querySelector(sel);
    const zipcodeSection =
      installationRoot?.querySelector('[data-zipcode-section]') ||
      document.querySelector('[data-zipcode-section]');
    const installationCheckbox = installationRoot?.querySelector('[data-installation-checkbox]');
    const installationAvailable = q('[data-installation-available]:not([data-addon-checkbox])');
    const installationNotAvailable = q('[data-installation-not-available]');
    const zipReset = q('[data-zip-reset]');
    const zipCodeBox = q('[data-zip-code-box]');
    const installCheck = installationRoot?.querySelector('#installCheck') || document.getElementById('installCheck');
    const assemblyElement = document.querySelector('[data-assembly-element]');
    const assemblyProperties = document.querySelectorAll('[data-assembly-property]');

    if (isAvailable) {
      if (window.globalVariables) window.globalVariables.installation_available = true;
      installationNotAvailable?.classList.add('d-none');
      installationAvailable?.classList.remove('d-none');
      if (installationCheckbox) {
        installationCheckbox.removeAttribute('disabled');
        installationCheckbox.checked = true;
      }
      if (installCheck) installCheck.classList.remove('d-none');
      if (assemblyElement && assemblyProperties.length) {
        const propertyValue = assemblyElement.getAttribute('data-product_assembly_handle');
        assemblyProperties.forEach((ele) => {
          ele.value = propertyValue || '';
        });
      }
    } else {
      if (window.globalVariables) {
        window.globalVariables.installation_available = false;
        window.globalVariables.zipcode = '';
      }
      installationAvailable?.classList.add('d-none');
      installationNotAvailable?.classList.remove('d-none');
      if (installationCheckbox) {
        installationCheckbox.checked = false;
        installationCheckbox.setAttribute('disabled', 'true');
      }
      if (assemblyElement && assemblyProperties.length) {
        const propertyValue = assemblyElement.getAttribute('data-product_handle');
        assemblyProperties.forEach((ele) => {
          ele.value = propertyValue || '';
        });
      }
    }

    if (zipCodeBox) {
      zipCodeBox.classList.remove('d-sm-flex', 'd-flex', 'd-md-flex', 'show-inline-transition', 'hide-inline-transition');
      zipCodeBox.classList.add('d-none');
    }

    if (isAvailable) {
      zipReset?.classList.remove('d-none');
    }

    if (window.__templateProductJS?._setAddToCartZipGateLocked) {
      window.__templateProductJS._setAddToCartZipGateLocked(
        !isAvailable && !!(installationCheckbox && installationCheckbox.checked)
      );
    }

    this.triggerAddonPricingUpdate(installationCheckbox || undefined);

    // _checkAddon hides zip/error UI when installation is unchecked — restore failed-zip state.
    if (!isAvailable && installationCheckbox) {
      if (zipcodeSection) {
        zipcodeSection.classList.remove('d-none');
        zipcodeSection.classList.add('active');
      }
      installationAvailable?.classList.add('d-none');
      installationNotAvailable?.classList.remove('d-none', 'hide-with-transition');
      installationNotAvailable?.classList.add('show-with-transition');
      zipReset?.classList.remove('d-none', 'hide-with-transition');
      zipReset?.classList.add('show-with-transition');
    }
  }

  resetInstallationZipcode(resetBtn) {
    this._installationZipValidationId += 1;

    const installationRoot = this.container.querySelector('[data-variant-installation-root]');
    const scope = resetBtn.closest('.zipcode-content') || installationRoot || this.container;
    const zipInput = scope.querySelector('[zip-value]');
    if (zipInput) zipInput.value = '';
    scope.querySelector('[data-installation-available]:not([data-addon-checkbox])')?.classList.add('d-none');
    scope.querySelector('[data-installation-not-available]')?.classList.add('d-none');
    resetBtn.classList.add('d-none');

    const installationCheckbox = installationRoot?.querySelector('[data-installation-checkbox]');
    if (installationCheckbox) {
      installationCheckbox.checked = false;
      installationCheckbox.removeAttribute('disabled');
    }

    const assemblyElement = document.querySelector('[data-assembly-element]');
    const assemblyProperties = document.querySelectorAll('[data-assembly-property]');
    if (assemblyElement && assemblyProperties.length) {
      const propertyValue = assemblyElement.getAttribute('data-product_handle');
      assemblyProperties.forEach((ele) => {
        ele.value = propertyValue || '';
      });
    }

    if (window.globalVariables) {
      window.globalVariables.installation_available = false;
      window.globalVariables.zipcode = '';
    }

    if (window.__templateProductJS?._setAddToCartZipGateLocked) {
      window.__templateProductJS._setAddToCartZipGateLocked(false);
    }

    this.syncInstallationZipUI(false);
    this.triggerAddonPricingUpdate();
    this.updatePackageSelection();
  }

  updatePackageSelection() {
    const packageSelection = document.querySelector('package-selection');
    if (!packageSelection || packageSelection.applyingAddons) return;
    if (window.__templateProductJS?._buyTogetherMountRefreshing) return;

    const mount = document.querySelector('[data-buy-together-variant-mount]') || this.container;
    const selectedHandles = [...mount.querySelectorAll('[data-addon-checkbox][data-product-handle]:checked')]
      .map((checkbox) => (checkbox.getAttribute('data-product-handle') || checkbox.getAttribute('name') || '').trim().toLowerCase())
      .filter(Boolean);
    const selectedSet = new Set(selectedHandles);

    const radios = [...packageSelection.querySelectorAll('input[type="radio"]')];
    const matchingRadio = radios.find((radio) => {
      const handles = (radio.getAttribute('data-addon-handles') || '')
        .split(',')
        .map((handle) => handle.trim().toLowerCase())
        .filter(Boolean);
      if (!handles.length || handles.length !== selectedSet.size) return false;
      return handles.every((handle) => selectedSet.has(handle));
    });

    if (matchingRadio?.checked) return;

    packageSelection.syncingFromAddons = true;
    try {
      radios.forEach((radio) => {
        radio.checked = radio === matchingRadio;
      });

      if (matchingRadio) {
        packageSelection.updateShowcaseMessage?.(matchingRadio);
        packageSelection.updateShowcaseHeading?.(matchingRadio);
        packageSelection.updateShowcaseWrapper?.(matchingRadio);
        packageSelection.updatePackageTitle?.(matchingRadio);
        return;
      }

      packageSelection.showDefaultShowcase?.();
      packageSelection.togglePackageTitle?.(false);
    } finally {
      packageSelection.syncingFromAddons = false;
    }
  }

  initCustomBundleSwitch() {
    const customBundleDataEl = document.querySelector('[data-custom-bundle]');
    if (!customBundleDataEl) return;

    let customBundles = [];
    try {
      const parsed = JSON.parse(customBundleDataEl.textContent);
      customBundles = Array.isArray(parsed) ? parsed : Object.entries(parsed).map(([variant, bundleData]) => ({
        variant,
        ...bundleData,
      }));
    } catch {
      return;
    }

    if (customBundles.length === 0) return;

    this._customBundles = customBundles;
    console.log('[BundleGallery] init', {
      bundleCount: customBundles.length,
      bundles: customBundles.map((bundle) => ({
        variant: bundle.variant,
        subheading: bundle.subheading,
        image: bundle.image,
        addon_handles: bundle.addon_handles,
      })),
    });

    const titleEl = this.container.querySelector('[data-variant-product-title-text]');
    this._originalProductTitle = titleEl ? titleEl.textContent : null;

    const getSelectedAddonHandles = () => {
      const buyTogetherPanel = this.container.querySelector('.product-buy-together[data-buy-together-block-id]');
      if (!buyTogetherPanel) return [];

      const checkedBoxes = buyTogetherPanel.querySelectorAll('.buy-together-card input[data-addon-checkbox]:checked');
      const handles = [];
      checkedBoxes.forEach((checkbox) => {
        const handle = checkbox.getAttribute('name') || checkbox.getAttribute('data-product-handle');
        if (handle) {
          handles.push(handle.trim().toLowerCase());
        }
      });
      return handles;
    };

    const findMatchingBundle = (variantId) => {
      const selectedHandles = getSelectedAddonHandles();
      const currentVariantId = String(variantId);
      const selectedCount = selectedHandles.length;

      console.log('[BundleGallery] match check', {
        variantId: currentVariantId,
        selectedHandles,
        selectedCount,
        availableBundles: customBundles.length,
      });

      if (selectedCount === 0) return null;

      for (const bundleData of customBundles) {
        if (!bundleData.addon_handles || bundleData.addon_handles.length === 0) continue;

        const bundleHandles = bundleData.addon_handles.map((h) => h.trim().toLowerCase());

        if (bundleHandles.length !== selectedCount) continue;
        if (String(bundleData.variant) !== currentVariantId) continue;

        const handlesMatch =
          bundleHandles.every((handle) => selectedHandles.includes(handle)) &&
          selectedHandles.every((handle) => bundleHandles.includes(handle));

        if (handlesMatch) {
          console.log('[BundleGallery] match found', {
            variantId: currentVariantId,
            bundleHandles,
            subheading: bundleData.subheading,
            image: bundleData.image,
          });
          return { bundleData };
        }
      }

      console.log('[BundleGallery] no match found');
      return null;
    };

    const getVariantTitleFromData = (variantId) => {
      const titleDataEl = document.querySelector('[data-variant-product-title-data]');
      if (!titleDataEl) return null;
      try {
        const titleMap = JSON.parse(titleDataEl.textContent);
        return titleMap[String(variantId)] || null;
      } catch {
        return null;
      }
    };

    const updateBundleDisplay = (retryCount = 0, trigger = 'unknown') => {
      const variantId = this.getSelectedVariantId();
      const titleEl = this.container.querySelector('[data-variant-product-title-text]');
      // const subheadingEl = this.container.querySelector('[data-custom-bundle-subheading]');

      console.log('[BundleGallery] updateBundleDisplay', {
        trigger,
        retryCount,
        variantId,
        applyBundleToGalleryReady: typeof this.applyBundleToGallery === 'function',
      });

      const match = findMatchingBundle(variantId);

      if (!match) {
        const variantTitle = getVariantTitleFromData(variantId);
        const resetTitle = variantTitle || this._originalProductTitle;
        if (titleEl && resetTitle) {
          titleEl.textContent = resetTitle;
        }
        // if (subheadingEl) {
        //   subheadingEl.textContent = '';
        //   subheadingEl.classList.add('d-none');
        // }

        if (typeof this.applyBundleToGallery === 'function') {
          const rendered = this.applyBundleToGallery(null);
          console.log('[BundleGallery] removed bundle image only', { rendered });
        }
        return;
      }

      const { bundleData } = match;

      if (titleEl && bundleData.subheading) {
        titleEl.textContent = bundleData.subheading;
      }

      // if (subheadingEl && bundleData.subheading) {
      //   subheadingEl.textContent = bundleData.subheading;
      //   subheadingEl.classList.remove('d-none');
      // }

      if (typeof this.applyBundleToGallery === 'function') {
        const rendered = this.applyBundleToGallery(bundleData);
        console.log('[BundleGallery] added bundle image only', {
          rendered,
          subheading: bundleData.subheading,
          image: bundleData.image,
        });

        if (!rendered && retryCount < 6) {
          setTimeout(() => updateBundleDisplay(retryCount + 1, trigger), 150);
          return;
        }

        if (rendered && document.querySelector('[data-custom-bundle-slide]')) {
          setTimeout(() => this.refreshSlider(), 50);
        }
      }
    };

    this.bindVariantChange(() => {
      setTimeout(() => updateBundleDisplay(0, 'variant-change'), 100);
    });

    const buyTogetherPanel = this.container.querySelector('.product-buy-together[data-buy-together-block-id]');
    
    if (buyTogetherPanel) {
      buyTogetherPanel.addEventListener('change', (event) => {
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('.buy-together-card')) return;
        if (!event.target.matches('input[data-addon-checkbox]')) return;
        
        setTimeout(() => updateBundleDisplay(0, 'addon-change'), 50);
      });
    }

    this.container.addEventListener('change', (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('.product-buy-together[data-buy-together-block-id]')) return;
      if (!event.target.closest('.buy-together-card')) return;
      if (!event.target.matches('input[data-addon-checkbox]')) return;

      setTimeout(() => updateBundleDisplay(0, 'addon-change-fallback'), 50);
    });

    this._updateCustomBundleDisplay = (trigger) => updateBundleDisplay(0, trigger || 'manual');

    setTimeout(() => updateBundleDisplay(0, 'initial-load'), 200);
  }

  refreshSlider() {
    const mainSwiper = this.getMainGallerySwiper();
    const thumbSwiper = this.getThumbGallerySwiper();

    console.log('[BundleGallery] refreshSlider', {
      mainSwiperFound: !!mainSwiper,
      thumbSwiperFound: !!thumbSwiper,
      mainSlideCount: mainSwiper?.slides?.length ?? null,
      activeIndex: mainSwiper?.activeIndex ?? null,
      firstSlideIsBundle: !!mainSwiper?.slides?.[0]?.hasAttribute('data-custom-bundle-slide'),
    });

    if (mainSwiper && typeof mainSwiper.update === 'function') {
      mainSwiper.update();
      mainSwiper.slideTo(0, 0, false);
    }

    if (thumbSwiper && typeof thumbSwiper.update === 'function') {
      thumbSwiper.update();
      thumbSwiper.slideTo(0, 0, false);
    }

    this.syncGalleryLightbox();
  }

  initAccessoriesFromUrlParams() {
    if (!this._urlAccessoryHandles || !this._urlAccessoryHandles.length) return;

    this._urlAccessoryHandlesDismissed = this._urlAccessoryHandlesDismissed || new Set();

    const getActiveUrlAccessoryHandles = () =>
      this._urlAccessoryHandles.filter((handle) => !this._urlAccessoryHandlesDismissed.has(handle));

    const getHandleFromCheckbox = (checkbox) => {
      if (!(checkbox instanceof HTMLInputElement)) return '';
      return (checkbox.getAttribute('name') || checkbox.getAttribute('data-product-handle') || '')
        .trim()
        .toLowerCase();
    };

    const notifyBundleDisplay = () => {
      if (typeof this._updateCustomBundleDisplay === 'function') {
        setTimeout(() => this._updateCustomBundleDisplay('url-accessories'), 50);
      }
    };

    const updateSelectedCount = (panel) => {
      if (!panel) return;

      const count = panel.querySelectorAll('.buy-together-card:not(.d-none) [data-addon-checkbox]:checked').length;
      const wrap = panel.querySelector('[data-buy-together-count-wrap]');
      if (wrap) {
        const valEl = wrap.querySelector('[data-buy-together-count-value]');
        if (valEl) valEl.textContent = String(count);
        if (count > 0) {
          wrap.removeAttribute('hidden');
        } else {
          wrap.setAttribute('hidden', '');
        }
      }
    };

    const selectAccessories = () => {
      const accessoryHandles = getActiveUrlAccessoryHandles();
      if (!accessoryHandles.length) return true;

      const buyTogetherPanel = this.container.querySelector('.product-buy-together[data-buy-together-block-id]');
      if (!buyTogetherPanel) return false;

      let anySelected = false;
      let allHandlesMatched = true;

      this._applyingUrlAccessories = true;

      accessoryHandles.forEach((handle) => {
        const checkbox = buyTogetherPanel.querySelector(
          `input[data-addon-checkbox][name="${handle}"], input[data-addon-checkbox][data-product-handle="${handle}"]`
        );

        if (!checkbox || checkbox.disabled) {
          allHandlesMatched = false;
          return;
        }

        if (!checkbox.checked) {
          checkbox.checked = true;
          anySelected = true;
        }

        const card = checkbox.closest('.buy-together-card[data-accessories-wrap]');
        if (card && !card.classList.contains('choose-selected')) {
          card.classList.add('choose-selected');
        }
      });

      this._applyingUrlAccessories = false;

      if (anySelected || allHandlesMatched) {
        if (anySelected) {
          this.openCollapsiblePanel(buyTogetherPanel);
        }

        accessoryHandles.forEach((handle) => {
          const checkbox = buyTogetherPanel.querySelector(
            `input[data-addon-checkbox][name="${handle}"], input[data-addon-checkbox][data-product-handle="${handle}"]`
          );
          if (checkbox && (anySelected || !checkbox.dataset.meridianUrlAccessorySynced)) {
            checkbox.dataset.meridianUrlAccessorySynced = 'true';
            checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        updateSelectedCount(buyTogetherPanel);

        if (window.__templateProductJS) {
          window.__templateProductJS._checkAddon?.({ currentTarget: buyTogetherPanel.querySelector('[data-addon-checkbox]:checked') });
          window.__templateProductJS._updateAccessoriesWoobtSaved?.();
          window.__templateProductJS._applyBuyTogetherTierPricing?.();
          window.__templateProductJS._updateBuyTogetherSelectedCount?.();
        }

        this.triggerAddonPricingUpdate();
        this.clearMeridianBuyTogetherDNone();
        updateSelectedCount(buyTogetherPanel);
        notifyBundleDisplay();
      }

      return allHandlesMatched;
    };

    const trackUrlAccessoryDismiss = (event) => {
      if (this._applyingUrlAccessories) return;
      if (!(event.target instanceof HTMLInputElement)) return;
      if (!event.target.matches('input[data-addon-checkbox]')) return;
      if (!event.target.closest('.product-buy-together[data-buy-together-block-id] .buy-together-card')) return;

      const handle = getHandleFromCheckbox(event.target);
      if (!handle || !this._urlAccessoryHandles.includes(handle)) return;

      if (event.target.checked) {
        this._urlAccessoryHandlesDismissed.delete(handle);
        return;
      }

      this._urlAccessoryHandlesDismissed.add(handle);
    };

    this.container.addEventListener('change', trackUrlAccessoryDismiss);

    const applyWithRetry = () => {
      selectAccessories();
    };

    applyWithRetry();
    requestAnimationFrame(applyWithRetry);
    requestAnimationFrame(() => requestAnimationFrame(applyWithRetry));

    setTimeout(applyWithRetry, 0);
    setTimeout(applyWithRetry, 100);
    setTimeout(applyWithRetry, 300);
    setTimeout(applyWithRetry, 500);
    setTimeout(applyWithRetry, 1000);
    setTimeout(applyWithRetry, 2000);
    setTimeout(applyWithRetry, 3000);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', applyWithRetry, { once: true });
    }

    window.addEventListener('load', () => {
      applyWithRetry();
      setTimeout(applyWithRetry, 100);
      setTimeout(applyWithRetry, 500);
      setTimeout(applyWithRetry, 1500);
    }, { once: true });

    document.addEventListener('variant:changed', () => {
      setTimeout(applyWithRetry, 100);
      setTimeout(applyWithRetry, 400);
    });

    const buyTogetherPanel = this.container.querySelector('.product-buy-together[data-buy-together-block-id]');
    if (buyTogetherPanel) {
      const observer = new MutationObserver(() => {
        if (selectAccessories()) {
          observer.disconnect();
        }
      });

      observer.observe(buyTogetherPanel, { childList: true, subtree: true, attributes: true });

      setTimeout(() => observer.disconnect(), 10000);
    }
  }
}

if (typeof MeridianGazeboBundleJS !== 'undefined') {
  window.__meridianBundleJS = new MeridianGazeboBundleJS();
}
